import type { Coding, Extension as FhirExtension } from '@medplum/fhirtypes'
import 'reflect-metadata'

const EXTENSION_METADATA_KEY = Symbol('extensions')
const TAG_METADATA_KEY = Symbol('tags')

export type AnyExtensionKind = keyof ExtensionDataTypes

export type ExtensionDataTypes<T = FhirExtension> = {
  [K in keyof T as K extends `value${infer _U}` ? K : never]: T[K]
}

interface ExtensionOptions<TKind extends AnyExtensionKind> {
  propertyKey: string
  url: string
  kind: TKind
  allowMultiple?: boolean
}

/**
 * Extension decorator to define a custom extension property.
 * @param options - Configuration options for the extension.
 */
export function Extension<TKind extends AnyExtensionKind>(
  options: Omit<ExtensionOptions<TKind>, 'propertyKey'>
) {
  return (target: any, propertyKey: string) => {
    if (!Reflect.hasOwnMetadata(EXTENSION_METADATA_KEY, target)) {
      Reflect.defineMetadata(EXTENSION_METADATA_KEY, [], target)
    }
    Reflect.getOwnMetadata(EXTENSION_METADATA_KEY, target).push({
      ...options,
      propertyKey,
    })
  }
}

/**
 * Get all extension metadata for a given target up the prototype chain.
 * For some reason, `Reflect.getMetadata` does not work here.
 * @param target - The target object to get the extension metadata for.
 * @returns An array of extension metadata.
 */
export function getExtensionMetadata(target: any): ExtensionOptions<any>[] {
  const metadata: ExtensionOptions<any>[] = []
  let currentTarget = target

  while (currentTarget) {
    const currentMetadata =
      Reflect.getOwnMetadata(EXTENSION_METADATA_KEY, currentTarget) ?? []
    metadata.push(...currentMetadata)
    currentTarget = Object.getPrototypeOf(currentTarget)
  }

  return metadata
}

interface TagOptions {
  system: string
  propertyKey: string
  allowMultiple?: boolean
}

type CodingFieldKeys<T> = {
  [K in keyof T]: T[K] extends Coding | Coding[] | undefined ? K : never
}[keyof T]

/**
 * Tag decorator to define a custom tag property.
 * @param options - Configuration options for the tag.
 */
export function Tag(options: Omit<TagOptions, 'propertyKey'>) {
  return <T extends object>(target: T, propertyKey: CodingFieldKeys<T>) => {
    if (!Reflect.hasOwnMetadata(TAG_METADATA_KEY, target)) {
      Reflect.defineMetadata(TAG_METADATA_KEY, [], target)
    }
    Reflect.getOwnMetadata(TAG_METADATA_KEY, target).push({
      ...options,
      propertyKey,
    })
  }
}

export function getTagMetadata(target: any): TagOptions[] {
  const metadata: TagOptions[] = []
  let currentTarget = target

  while (currentTarget) {
    const currentMetadata =
      Reflect.getOwnMetadata(TAG_METADATA_KEY, currentTarget) ?? []
    metadata.push(...currentMetadata)
    currentTarget = Object.getPrototypeOf(currentTarget)
  }
  return metadata
}
